/**
 * @file Provides a function that counts the files and transactions attached to a transfer row's investment.
 */
import { useCallback } from "react";

import type { TransferInvestment } from "../types";

type Attachment = {
  investmentId?: string;
};

type CountedInvestment = Pick<TransferInvestment, "id" | "isIncluded" | "sourceInvestmentId">;

type Options = {
  sourceFiles: Attachment[];
  sourceTransactions: Attachment[];
};

export type GetInvestmentAttachmentsCount = (investment: CountedInvestment) => number | string | null;

/**
 * Returns a function that counts an investment's attached files and transactions, from its own or a
 * migrated source investment's attachments.
 */
const useAttachmentsCount = ({
  sourceFiles,
  sourceTransactions,
}: Options): GetInvestmentAttachmentsCount => {
  const getInvestmentAttachmentsCount = useCallback((investment: CountedInvestment) => {
    const { id, isIncluded, sourceInvestmentId } = investment;

    if (!isIncluded) {
      return null;
    }

    if (!sourceInvestmentId) {
      const sourceAttachmentsCount = [ ...sourceFiles, ...sourceTransactions ]
        .filter(({ investmentId }) => investmentId === id)
        .length;

      return sourceAttachmentsCount > 0 ? sourceAttachmentsCount : null;
    }

    const migratedAttachmentsCount = [ ...sourceFiles, ...sourceTransactions ]
      .filter(({ investmentId }) => investmentId === sourceInvestmentId)
      .length;

    return migratedAttachmentsCount > 0 ? `+${migratedAttachmentsCount}` : null;
  }, [
    sourceFiles,
    sourceTransactions,
  ]);

  return getInvestmentAttachmentsCount;
};

export default useAttachmentsCount;
